/**
 * Shipping API client.
 *
 * Network-layer foundation for every FE-SHIP-* ticket. One named export
 * per backend endpoint (six total — one customer-public rate quote, one
 * customer tracking lookup, four admin shipment + damage-claim
 * operations). Each function:
 *
 *   1. Calls the configured shared axios instance with the right path,
 *      method, and body — no per-call auth headers, no per-call baseURL,
 *      no envelope assembly. The interceptor on `apiClient` injects the
 *      bearer token, so admin-scoped routes ride the same channel as
 *      customer routes.
 *   2. Unwraps the success envelope into a typed domain value
 *      (`ShippingRateDTO[]`, `ShipmentDTO`, etc.). The platform's
 *      shipping templates emit snake_case at the wire (e.g.
 *      `tracking_number`, `label_s3_key`); the helpers below transform
 *      to camelCase so the slice + selectors only ever see the
 *      camelCase DTO shape declared in
 *      `@dfq/shared/types/shipping`.
 *   3. Translates any non-2xx response into an {@link ApiError} that
 *      preserves the platform envelope's `status`, `severity`, `message`,
 *      `errors` map (422), and `error_code`.
 *
 * The customer-facing rate-quote and tracking endpoints sit under the
 * top-level prefix; the four admin endpoints sit under `/admin/`. Both
 * groups go through the same axios instance — guard / route-mounting is a
 * platform concern, not a frontend one.
 *
 * SH-SHIP-001.
 */
import type { ApiEnvelope } from '@dfq/shared/types/auth';
import type {
  CreateShipmentPayload,
  FetchCustomerShippingRatesPayload,
  FetchShippingRatesPayload,
  OrderTrackingDTO,
  ShipmentDTO,
  ShippingCarrier,
  ShippingDamageClaimDTO,
  ShippingDamageClaimStatus,
  ShippingPaginationMeta,
  ShippingRateDTO,
  ShippingRatesResponseData,
  TrackingEventDTO,
  UpdateDamageClaimStatusPayload,
} from '@dfq/shared/types/shipping';

import { apiClient } from './client';
import { throwAsApiError, unwrap } from './_envelope';

// ----------------------------------------------------------------------------
// Path builders (private)
// ----------------------------------------------------------------------------

function shippingRatesPath(): string {
  return '/shipping/rates';
}

function orderTrackingPath(orderId: number): string {
  return `/account/orders/${encodeURIComponent(String(orderId))}/tracking`;
}

function adminOrderShipmentsPath(orderId: number): string {
  return `/admin/orders/${encodeURIComponent(String(orderId))}/shipments`;
}

function adminDamageClaimsPath(): string {
  return '/admin/damage-claims';
}

function adminDamageClaimPath(claimId: number): string {
  return `/admin/damage-claims/${encodeURIComponent(String(claimId))}`;
}

// ----------------------------------------------------------------------------
// Wire-shape interfaces — snake_case as emitted by the platform templates.
// Kept private; callers consume the camelCase DTOs from
// `@dfq/shared/types/shipping`.
// ----------------------------------------------------------------------------

interface ShippingRateWire {
  carrier: ShippingCarrier;
  service_level: string;
  price_cents: number;
  estimated_days: number;
}

interface ShipmentDimensionsWire {
  length_inches: number | string;
  width_inches: number | string;
  height_inches: number | string;
}

interface ShipmentWire {
  id: number;
  order_id: number;
  carrier: ShippingCarrier;
  service_level: string;
  tracking_number: string | null;
  label_s3_key: string | null;
  shipped_at: string | null;
  estimated_delivery_at: string | null;
  delivered_at: string | null;
  cost_cents: number;
  weight_ounces: number | string;
  dimensions: ShipmentDimensionsWire | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Admin shipment endpoints return `ShipmentDTO::toArray()` directly, which is
 * camelCase; only the nested `dimensions` JSON keeps its snake_case keys.
 */
interface AdminShipmentWire {
  id: number;
  orderId: number;
  carrier: ShippingCarrier;
  serviceLevel: string | null;
  trackingNumber: string | null;
  shippedAt: string | null;
  estimatedDeliveryAt: string | null;
  deliveredAt: string | null;
  shippingCost: number | string | null;
  weightOz: number | string | null;
  dimensions: ShipmentDimensionsWire | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

interface TrackingEventWire {
  occurred_at: string;
  status: string;
  description: string;
  location: string | null;
}

interface DamageClaimWire {
  id: number;
  order_id: number;
  shipment_id: number | null;
  status: ShippingDamageClaimStatus;
  description: string;
  photo_s3_keys: string[];
  resolution_notes: string | null;
  submitted_at: string;
  reviewed_at: string | null;
  resolved_at: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface PaginationWire {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

// Envelope projections — the platform's shipping templates return data
// keyed by the resource name, e.g. `data.rates`, `data.shipment`. Keeping
// these private types pinned to one place avoids drift if a future ticket
// renames an envelope key.

interface RateListData {
  rates: ShippingRateWire[];
}

interface OrderTrackingData {
  shipment: ShipmentWire;
  events: TrackingEventWire[];
}

interface ShipmentListData {
  shipments: AdminShipmentWire[];
}

interface SingleShipmentData {
  shipment: AdminShipmentWire;
}

interface DamageClaimListData {
  claims: DamageClaimWire[];
}

interface SingleDamageClaimData {
  claim: DamageClaimWire;
}

// ----------------------------------------------------------------------------
// Wire-shape → DTO transforms
// ----------------------------------------------------------------------------

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  return typeof value === 'number' ? value : Number(value);
}

function fromRateWire(wire: ShippingRateWire): ShippingRateDTO {
  return {
    carrier: wire.carrier,
    serviceLevel: wire.service_level,
    priceCents: wire.price_cents,
    estimatedDays: wire.estimated_days,
  };
}

function fromShipmentWire(wire: ShipmentWire): ShipmentDTO {
  return {
    id: wire.id,
    orderId: wire.order_id,
    carrier: wire.carrier,
    serviceLevel: wire.service_level,
    trackingNumber: wire.tracking_number,
    labelS3Key: wire.label_s3_key,
    shippedAt: wire.shipped_at,
    estimatedDeliveryAt: wire.estimated_delivery_at,
    deliveredAt: wire.delivered_at,
    costCents: wire.cost_cents,
    weightOunces: toNumber(wire.weight_ounces),
    // dimensions_json is nullable on the platform side — a manually recorded
    // shipment need not carry a box size — and reading through it unguarded
    // threw, which surfaced as the whole shipments panel failing to render
    // rather than as one missing field.
    dimensions: {
      lengthInches: toNumber(wire.dimensions?.length_inches),
      widthInches: toNumber(wire.dimensions?.width_inches),
      heightInches: toNumber(wire.dimensions?.height_inches),
    },
    createdAt: wire.created_at ?? null,
    updatedAt: wire.updated_at ?? null,
  };
}

function fromAdminShipmentWire(wire: AdminShipmentWire): ShipmentDTO {
  return {
    id: wire.id,
    orderId: wire.orderId,
    carrier: wire.carrier,
    serviceLevel: wire.serviceLevel ?? '',
    trackingNumber: wire.trackingNumber,
    labelS3Key: null,
    shippedAt: wire.shippedAt,
    estimatedDeliveryAt: wire.estimatedDeliveryAt,
    deliveredAt: wire.deliveredAt,
    costCents: Math.round(toNumber(wire.shippingCost) * 100),
    weightOunces: toNumber(wire.weightOz),
    dimensions: {
      lengthInches: toNumber(wire.dimensions?.length_inches),
      widthInches: toNumber(wire.dimensions?.width_inches),
      heightInches: toNumber(wire.dimensions?.height_inches),
    },
    createdAt: wire.createdAt ?? null,
    updatedAt: wire.updatedAt ?? null,
  };
}

function fromTrackingEventWire(wire: TrackingEventWire): TrackingEventDTO {
  return {
    occurredAt: wire.occurred_at,
    status: wire.status,
    description: wire.description,
    location: wire.location,
  };
}

function fromDamageClaimWire(wire: DamageClaimWire): ShippingDamageClaimDTO {
  return {
    id: wire.id,
    orderId: wire.order_id,
    shipmentId: wire.shipment_id,
    status: wire.status,
    description: wire.description,
    photoS3Keys: wire.photo_s3_keys,
    resolutionNotes: wire.resolution_notes,
    submittedAt: wire.submitted_at,
    reviewedAt: wire.reviewed_at,
    resolvedAt: wire.resolved_at,
    createdAt: wire.created_at ?? null,
    updatedAt: wire.updated_at ?? null,
  };
}

function fromPaginationWire(wire: PaginationWire): ShippingPaginationMeta {
  return {
    currentPage: wire.current_page,
    perPage: wire.per_page,
    total: wire.total,
    lastPage: wire.last_page,
  };
}

// ----------------------------------------------------------------------------
// Customer-facing — rate quote
// ----------------------------------------------------------------------------

/**
 * `POST /api/v1/shipping/rates`
 *
 * Quote a multi-carrier rate set for the authenticated customer's cart at
 * the given destination zip. Returns an array of options ordered by the
 * platform's preferred carrier policy (typically cheapest-first).
 *
 * Empty array is a legitimate response when no carrier can serve the
 * destination — the slice surfaces it as "no rates available," not an
 * error. Validation failures (missing zip, unknown cart) surface as 422
 * `ApiError` with the field-keyed `errors` map.
 */
export async function fetchShippingRates(
  payload: FetchShippingRatesPayload,
): Promise<ShippingRateDTO[]> {
  try {
    const response = await apiClient.post<ApiEnvelope<RateListData>>(
      shippingRatesPath(),
      payload,
    );
    const data = unwrap(response.data, response.status);
    return (data.rates ?? []).map(fromRateWire);
  } catch (error) {
    throwAsApiError(error);
  }
}

// ----------------------------------------------------------------------------
// Customer-facing — order tracking lookup
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/orders/{orderId}/tracking`
 *
 * Returns the shipment + carrier-event feed for an order the authenticated
 * customer owns. 404 surfaces as `ApiError` when the customer does not own
 * the order or no shipment has been created yet. The slice keys the
 * resulting payload by `orderId` so multiple in-flight tracking views
 * stay isolated.
 */
export async function fetchOrderTracking(orderId: number): Promise<OrderTrackingDTO> {
  try {
    const response = await apiClient.get<ApiEnvelope<OrderTrackingData>>(
      orderTrackingPath(orderId),
    );
    const data = unwrap(response.data, response.status);
    return {
      shipment: fromShipmentWire(data.shipment),
      events: (data.events ?? []).map(fromTrackingEventWire),
    };
  } catch (error) {
    throwAsApiError(error);
  }
}

// ----------------------------------------------------------------------------
// Admin — shipment list / create
// ----------------------------------------------------------------------------

/**
 * `GET /api/v1/admin/orders/{orderId}/shipments`
 *
 * Returns every shipment row attached to an order — split shipments and
 * historical replacement labels alike. Empty array is a valid response
 * when the order has not been shipped yet.
 */
export async function fetchOrderShipments(orderId: number): Promise<ShipmentDTO[]> {
  try {
    const response = await apiClient.get<ApiEnvelope<ShipmentListData>>(
      adminOrderShipmentsPath(orderId),
    );
    const data = unwrap(response.data, response.status);
    return (data.shipments ?? []).map(fromAdminShipmentWire);
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `POST /api/v1/admin/orders/{orderId}/shipments`
 *
 * Records a manually dispatched shipment (no label is generated). The
 * platform also advances the order to `shipped`, so callers should refetch
 * the order afterwards.
 */
export async function createOrderShipment(
  orderId: number,
  payload: CreateShipmentPayload,
): Promise<ShipmentDTO> {
  try {
    const response = await apiClient.post<ApiEnvelope<SingleShipmentData>>(
      adminOrderShipmentsPath(orderId),
      payload,
    );
    const data = unwrap(response.data, response.status);
    return fromAdminShipmentWire(data.shipment);
  } catch (error) {
    throwAsApiError(error);
  }
}

// ----------------------------------------------------------------------------
// Admin — damage claims
// ----------------------------------------------------------------------------

/**
 * Optional list filters for `GET /api/v1/admin/damage-claims`. The platform
 * accepts page + per_page for cursor-style pagination plus an optional
 * status filter (the admin claims list defaults to "all open" but supports
 * narrowing).
 */
export interface ListDamageClaimsParams {
  page?: number;
  perPage?: number;
  status?: ShippingDamageClaimStatus;
}

/**
 * Wrapper for the paginated damage-claim list — the slice stores both the
 * rows AND the pagination meta so containers can render page controls
 * without re-fetching.
 */
export interface ListDamageClaimsResult {
  claims: ShippingDamageClaimDTO[];
  pagination: ShippingPaginationMeta | null;
}

/**
 * `GET /api/v1/admin/damage-claims`
 *
 * Lists damage claims with server-side pagination. Pagination metadata
 * lives on `meta.pagination` per the platform's standard envelope.
 */
export async function fetchDamageClaims(
  params: ListDamageClaimsParams = {},
): Promise<ListDamageClaimsResult> {
  try {
    const response = await apiClient.get<ApiEnvelope<DamageClaimListData>>(
      adminDamageClaimsPath(),
      {
        params: {
          ...(params.page !== undefined ? { page: params.page } : {}),
          ...(params.perPage !== undefined ? { per_page: params.perPage } : {}),
          ...(params.status !== undefined ? { status: params.status } : {}),
        },
      },
    );
    const data = unwrap(response.data, response.status);
    const meta = response.data.meta as
      | { pagination?: PaginationWire }
      | undefined;
    return {
      claims: (data.claims ?? []).map(fromDamageClaimWire),
      pagination: meta?.pagination ? fromPaginationWire(meta.pagination) : null,
    };
  } catch (error) {
    throwAsApiError(error);
  }
}

/**
 * `PUT /api/v1/admin/damage-claims/{claimId}/status`
 *
 * Updates a claim's status (and optionally its resolution notes). The
 * slice mirrors the new status optimistically before this call resolves;
 * a non-2xx response triggers a rollback in the `rejected` reducer case.
 */
export async function updateDamageClaimStatus(
  claimId: number,
  payload: UpdateDamageClaimStatusPayload,
): Promise<ShippingDamageClaimDTO> {
  try {
    const response = await apiClient.put<ApiEnvelope<SingleDamageClaimData>>(
      `${adminDamageClaimPath(claimId)}/status`,
      payload,
    );
    const data = unwrap(response.data, response.status);
    return fromDamageClaimWire(data.claim);
  } catch (error) {
    throwAsApiError(error);
  }
}

// ----------------------------------------------------------------------------
// SF-SHIP-001 — customer-facing rate request
//
// The customer storefront posts a richer payload (`zip_code`,
// optional `cart_item_ids[]`) and receives a richer wire shape (rates
// carry recommendation / rush / free-shipping-eligible / surcharge
// flags). The two helpers coexist intentionally — `fetchShippingRates`
// above feeds the admin/leaner DTO; this one feeds the storefront
// checkout selector.
// ----------------------------------------------------------------------------

interface CustomerRateListWire {
  rates: import('@dfq/shared/types/shipping').ShippingRate[];
}

/**
 * `POST /api/v1/shipping/rates` (customer-facing variant).
 *
 * Fetches the multi-carrier rate set for a destination zip and the
 * authenticated customer's cart. Returns the unwrapped envelope
 * `data` payload so thunks don't have to repeat the envelope plumbing.
 *
 * Wire shape is snake_case (`is_recommended`, `is_free_shipping_eligible`,
 * etc.); the storefront container layer adapts it into the capsule's
 * view shape.
 */
export async function fetchShippingRatesRequest(
  payload: FetchCustomerShippingRatesPayload,
): Promise<ShippingRatesResponseData> {
  const response = await apiClient.post<ApiEnvelope<CustomerRateListWire>>(
    '/shipping/rates',
    payload,
  );
  // The customer-facing endpoint emits the same envelope shape as the
  // admin-facing one but does NOT camelCase its rate rows; we hand back
  // the wire-shape payload directly so the storefront slice can persist
  // it unchanged.
  const envelope = response.data;
  if (envelope.status !== 'success' || !envelope.data) {
    throw new Error(envelope.message ?? 'Failed to fetch shipping rates');
  }
  return { rates: envelope.data.rates ?? [] };
}

/**
 * Convenience namespace export for callers that prefer
 * `import { shippingApi } from '@dfq/shared/api/shipping'`.
 */
export const shippingApi = {
  fetchShippingRates,
  fetchOrderTracking,
  fetchOrderShipments,
  createOrderShipment,
  fetchDamageClaims,
  updateDamageClaimStatus,
  fetchShippingRatesRequest,
};
